import React from 'react';
import { connect } from 'react-redux';
import * as R from 'ramda';
import { bindActionCreators, Dispatch } from 'redux';

import * as inventoryAssetActionCreators from '@atom/actions/inventoryAssetActions';
import { Modal, Progress, Snackbar } from '@atom/mui';
import { InventoryAssetActions } from '@atom/types/actions';
import {
  AssetDetailView,
  InventoryAssetDetailType,
} from '@atom/types/inventory';
import { PolicyAction } from '@atom/types/policy';
import { ReduxStore } from '@atom/types/store';
import { hasAccess } from '@atom/utilities/accessUtilities';
import accountUtilities from '@atom/utilities/accountUtilities';
import api from '@atom/utilities/api';
import { INVENTORY_ASSETS_ENDPOINT } from '@atom/utilities/endpoints';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import AnalyticsTab from './bodyPanes/analytics/AnalyticsTab';
import CostTab from './bodyPanes/cost/CostTab';
import DetailTab from './bodyPanes/detail/DetailTab';
import Folders from './bodyPanes/folders/Folders';
import InventoryAssetInfo from './bodyPanes/info/InventoryAssetInfo';
import InventoryPhotos from './bodyPanes/inventoryPhotos/InventoryPhotos';
import RequestsTab from './bodyPanes/requests/RequestsTab';
import AssetWorkOrdersContainer from './bodyPanes/workOrders/AssetWorkOrdersContainer';
import InventoryDetailHeader from './header/InventoryDetailHeader';

import classes from './detail.module.css';

const EDITABLE_ERROR_CODE = 422;

interface PassedProps {
  id: string;
  navigate: any;
  searchParamsObject: any;
  updateSearchParams: any;
  backDestination: (defaultUrl?: string) => any;
}

interface ReduxStateProps {
  loading: boolean;
  inventoryAssetDetail?: InventoryAssetDetailType;
}

interface ReduxDispatchProps {
  inventoryAssetActions: InventoryAssetActions;
}

type Props = ReduxStateProps & ReduxDispatchProps & PassedProps;

interface State {
  inventoryMoveDisabled: boolean;
  openErrorModal: boolean;
}

class InventoryDetail extends React.Component<Props, State> {
  state = {
    inventoryMoveDisabled: false,
    openErrorModal: false,
  };

  componentDidMount() {
    const { inventoryAssetActions, id, searchParamsObject } = this.props;

    if (!searchParamsObject.activeView) {
      this.setActiveView(AssetDetailView.ASSET_INFO);
    }

    inventoryAssetActions.retrieveInventoryAssetDetail({ id });
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.id !== this.props.id) {
      const { inventoryAssetActions } = this.props;
      inventoryAssetActions.retrieveInventoryAssetDetail({
        id: nextProps.id,
      });
    }
  }

  componentWillUnmount() {
    Snackbar.dismiss();
  }

  setActiveView = (view: AssetDetailView) => {
    const { updateSearchParams } = this.props;
    updateSearchParams({
      activeView: view || AssetDetailView.ASSET_INFO,
    });
  };

  navigateBack = () => {
    this.props.navigate(this.props.backDestination('/inventory'));
  };

  onAssetEdit = (
    id: string,
    name: string,
    rate: number,
    unit?: string,
    isStockBased?: boolean,
    isStartEndReading?: boolean,
    isStartEndCalculated?: boolean,
    quantityRemaining?: number,
  ) => {
    const { inventoryAssetActions, inventoryAssetDetail } = this.props;

    inventoryAssetActions.requestInventoryAssetDetailUpdate({
      name,
      id,
      ...(inventoryAssetDetail.isMaterial && {
        budget: {
          rate,
          ...(unit && { unit }),
          ...(!isNilOrEmpty(isStockBased) && { isStockBased }),
          ...(!isNilOrEmpty(isStartEndReading) && { isStartEndReading }),
          ...(!isNilOrEmpty(isStartEndCalculated) && { isStartEndCalculated }),
          ...(!isNilOrEmpty(quantityRemaining) && { quantityRemaining }),
        },
      }),
    });
  };

  onSelectedChange = (selected: string[]) => {
    this.setState({
      inventoryMoveDisabled: Boolean(selected.length),
    });
  };

  setDetailBodyPane = activeView => {
    const { inventoryAssetDetail, id } = this.props;

    const canCreateMedia = hasAccess(
      PolicyAction.CREATE_MEDIA,
      inventoryAssetDetail.actions,
    );

    const canUpdateMedia = hasAccess(
      PolicyAction.UPDATE_MEDIA,
      inventoryAssetDetail.actions,
    );

    const canDeleteMedia = hasAccess(
      PolicyAction.DELETE_MEDIA,
      inventoryAssetDetail.actions,
    );

    const canMarkAsCoverPhoto = hasAccess(
      PolicyAction.UPDATE,
      inventoryAssetDetail.actions,
    );

    const content = {
      work: (
        <AssetWorkOrdersContainer
          assetId={inventoryAssetDetail.id}
          schemaId={inventoryAssetDetail.schemaId}
        />
      ),
      files: (
        <Folders
          id={id}
          subjectId={inventoryAssetDetail.id}
          subjectType="inventoryAsset"
          onSelectedChange={this.onSelectedChange}
          canCreateMedia={canCreateMedia}
          canUpdateMedia={canUpdateMedia}
          canDeleteMedia={canDeleteMedia}
        />
      ),
      photos: (
        <InventoryPhotos
          inventoryAssetDetail={inventoryAssetDetail}
          canCreateMedia={canCreateMedia}
          canMarkAsCoverPhoto={canMarkAsCoverPhoto}
          canUpdateMedia={canUpdateMedia}
          canDeleteMedia={canDeleteMedia}
        />
      ),
      assetInfo: <InventoryAssetInfo />,
      subItems: <DetailTab />,
      cost: <CostTab asset={inventoryAssetDetail} />,
      // TODO: AM-7339 Remove analytics tab component
      analytics: <AnalyticsTab />,
      requests: <RequestsTab asset={inventoryAssetDetail} />,
    };

    return content[activeView];
  };

  onApproveAllPendingChanges = async () => {
    const { inventoryAssetActions, inventoryAssetDetail } = this.props;

    try {
      const endpoint = `${INVENTORY_ASSETS_ENDPOINT}/${inventoryAssetDetail.id}/approve`;
      const payload = {
        includeChangesBefore: inventoryAssetDetail.includeChangesBefore,
        rootAssetId: inventoryAssetDetail.id,
      };

      await api.post(endpoint, payload);

      const rootAssetEndpoint = `${INVENTORY_ASSETS_ENDPOINT}/${inventoryAssetDetail.id}`;
      const { data } = await api.get(rootAssetEndpoint);

      inventoryAssetActions.getInventoryAssetDetailSuccess(data);
    } catch (error) {
      if (error?.response?.status === EDITABLE_ERROR_CODE) {
        this.setState({ openErrorModal: true });
      }

      accountUtilities.apiErrorHandler(
        error,
        inventoryAssetActions.getInventoryAssetsFailure(),
      );
    }
  };

  render() {
    const { inventoryAssetDetail, loading, searchParamsObject } = this.props;
    const { inventoryMoveDisabled, openErrorModal } = this.state;

    const activeView: AssetDetailView =
      (searchParamsObject.activeView as AssetDetailView) ||
      AssetDetailView.ASSET_INFO;

    if (loading || R.isEmpty(inventoryAssetDetail)) {
      return (
        <div className={classes.spinnerContainer}>
          <Progress />
        </div>
      );
    }

    return (
      <>
        <div className={classes.inventoryDetailContainer}>
          <InventoryDetailHeader
            inventoryAssetDetail={inventoryAssetDetail}
            loadingApproveAll={loading}
            activeView={activeView}
            setActiveView={this.setActiveView}
            navigateBack={this.navigateBack}
            onAssetEdit={this.onAssetEdit}
            onApproveAllPendingChanges={this.onApproveAllPendingChanges}
            inventoryMoveDisabled={inventoryMoveDisabled}
          />
          {this.setDetailBodyPane(activeView)}
        </div>
        <Modal
          open={openErrorModal}
          confirmButtonText="OK"
          onConfirm={() => this.setState({ openErrorModal: false })}
          title="Cannot Approve All Changes"
        >
          Some pending changes cannot be approved because the attribute is not
          editable. Please set the attribute to editable in the asset schema to
          approve or reject changes.
        </Modal>
      </>
    );
  }
}

const mapStateToProps = (state: ReduxStore): ReduxStateProps => ({
  inventoryAssetDetail: state.inventoryAssetDetail,
  loading: state.loading.loadingInventoryAssetDetail,
});

const mapDispatchToProps = (dispatch: Dispatch): ReduxDispatchProps => ({
  inventoryAssetActions: bindActionCreators(
    inventoryAssetActionCreators,
    dispatch,
  ),
});

export default connect(mapStateToProps, mapDispatchToProps)(InventoryDetail);
